import { observer } from "mobx-react-lite"
import { makeAutoObservable, toJS } from "mobx"
import { useEffect, useMemo } from "react"
import { match } from "ts-pattern"
import {
  PersonalDetails,
  ProfileInformation,
  AgencyInformation,
  SlackInformation,
  Subscription,
} from "./steps"
import { getAuth } from "firebase/auth"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createUser, updateUser } from "../../../services/firestore/user"
import {
  AgencyCruisesAnnualBookingVolume,
  AgencyGoalsAtCruisen,
  AgencyPrimaryCustomerAcquisitionMean,
  AgentCruisesOverallVolumePercent,
  AgentPlanEnum,
  AgentProfilePayload,
  CruisePassengerAgeGroup,
  ITrip,
  IUser,
  UserPayload,
} from "@cruisen/contract"
import { UserModel } from "@cruisen/react-shared"
import { useStores } from "../../../models/helpers/useStores"
import { useNavigate, useSearchParams } from "react-router-dom"
import PersonalDetailsImage from "../../../assets/personalDetailsImage.png"
import ProfileInformationImage from "../../../assets/profileInformationImage.png"
import AgencyInformationImage from "../../../assets/agencyInformationImage.png"
import SlackInformationImage from "../../../assets/slackInformationImage.png"
import SubscriptionImage from "../../../assets/subscriptionImage.png"
import FormStepCard from "./components/FormStepCard"
import { prepareDemoTrip } from "../../../services/firestore/developerFunctions"
import { upsertAgentProfile } from "../../../services/firebase-functions/agentProfile"
import { getUserTrips } from "../../../services/firestore/trips"
import { Analytics } from "../../../utils/analytics"
import { AgencySurvey } from "./steps/AgencySurvey"
import { generateAgentLink } from "../../../utils/agentLink"
import {
  createStripeSubscription,
  getStripeConfig,
} from "../../../services/firebase-functions/billing"
import { load } from "../../../utils/storage.ts"
import { isSubscriptionEnabled } from "../../../services/firestore/billing.ts"
import Config from "../../../config"
import { registerUser } from "../../../services/firebase-functions/user.ts"
import { firebaseAuthUsingToken } from "../../../services/auth.ts"

export type AvailableSteps =
  | "personalDetails"
  | "profileInformation"
  | "agencyInformation"
  | "agencySurvey"
  | "subscription"
  | "creditCardDetails"
  | "slackInformation"

type FormUserData = {
  firstName: string
  lastName: string
  phoneNumber: string
  shortBio: string
  favoriteTravelDestination: string
  cruisesOverallVolumePercent?: AgentCruisesOverallVolumePercent
  cruisesAnnualBookingVolume?: AgencyCruisesAnnualBookingVolume
  primaryCustomerAcquisitionMean?: AgencyPrimaryCustomerAcquisitionMean
  agencyGoalsAtCruisen?: AgencyGoalsAtCruisen[]
  bestTravelAdvice: string
  avatar: string
  agencyName: string
  agencyPhoneNumber: string
  hostAgencyId: string
  consortiaId: string
}

export class SignupPageStore {
  currentStep: AvailableSteps
  userData: Partial<FormUserData>
  isUserSignuped: boolean
  isPendingRequest: boolean
  createdUser: IUser | null
  termsAccepted: boolean

  constructor() {
    this.currentStep = "personalDetails"
    this.userData = {}
    this.isUserSignuped = false
    this.isPendingRequest = false
    this.createdUser = null
    this.termsAccepted = false

    makeAutoObservable(this)
  }

  setCreatedUser(user: IUser | null) {
    this.createdUser = user
  }

  setIsPendingRequest(isPendingRequest: boolean) {
    this.isPendingRequest = isPendingRequest
  }

  setCurrentStep(step: AvailableSteps) {
    this.currentStep = step
  }

  setUserData(userData: Partial<FormUserData>) {
    this.userData = { ...this.userData, ...userData }
  }

  setIsUserSignuped(isUserSignuped: boolean) {
    this.isUserSignuped = isUserSignuped
  }

  setTermsAccepted(termsAccepted: boolean) {
    this.termsAccepted = termsAccepted
  }
}

const variants: {
  step: AvailableSteps
  image: string
  title: string
  description: string
  stepIndex: number
}[] = [
  {
    step: "personalDetails",
    image: PersonalDetailsImage,
    title: "Welcome aboard",
    description: "Share your details and let's get started.",
    stepIndex: 0,
  },
  {
    step: "profileInformation",
    image: ProfileInformationImage,
    title: "About you",
    description: "Your story, your goals—share a quick intro.",
    stepIndex: 1,
  },
  {
    step: "agencyInformation",
    image: AgencyInformationImage,
    title: "About your agency",
    description: "Share your agency's contact information.",
    stepIndex: 2,
  },
  {
    step: "agencySurvey",
    image: ProfileInformationImage,
    title: "Agency survey",
    description: "Let's get to know each other a little bit.",
    stepIndex: 3,
  },
  {
    step: "subscription",
    image: SubscriptionImage,
    title: "Enjoy Your Trial",
    description: `Enjoy Cruisen at no cost for ${Config.stripeDefaultTrialPeriodDays} days.`,
    stepIndex: 4,
  },
  {
    step: "slackInformation",
    image: SlackInformationImage,
    title: "All aboard!",
    description: "We're setting up your account.",
    stepIndex: 5,
  },
]

const SignUpForm = observer(function SignUpForm() {
  const store = useMemo(() => new SignupPageStore(), [])
  const currentUser = getAuth().currentUser
  const rootStore = useStores()
  const navigate = useNavigate()
  const currentVariant = variants.find((variant) => variant.step === store.currentStep)
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: stripeConfig } = useQuery({
    queryKey: ["stripeConfig", currentUser?.uid],
    queryFn: () => currentUser?.uid && getStripeConfig(),
  })
  const stripeConfigPrice = stripeConfig?.prices?.[0]

  const { mutate: mutateRegisterUser, isPending: isUserRegistrationPending } = useMutation({
    mutationFn: async (userData: Partial<FormUserData>) => {
      const agentCode = currentUser?.uid

      const userPayload: UserPayload = {
        id: currentUser?.uid as string,
        email: currentUser?.email as string,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        phoneNumber: userData.phoneNumber || "",
        ageGroup: "adult" as CruisePassengerAgeGroup,
        avatar: userData.avatar || "",
        referralCode: "",
        agentCode,
      }

      const { user: newUser, authToken } = await registerUser({ userPayload })

      // a new user was merged with the existing user, so existing user auth token is used
      if (authToken) {
        await firebaseAuthUsingToken(authToken)
        window.location.reload()
        return
      }

      rootStore.setProp("loggedInUser", UserModel.create(newUser))
      store.setCurrentStep("profileInformation")
    },
  })

  const { mutate: mutateUpsertUser, isPending: isUserUpsertPending } = useMutation({
    mutationFn: async (userData: Partial<FormUserData>) => {
      const agentCode = currentUser?.uid

      const userPayload: UserPayload = {
        id: currentUser?.uid as string,
        email: currentUser?.email as string,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        phoneNumber: userData.phoneNumber || "",
        ageGroup: "adult" as CruisePassengerAgeGroup,
        avatar: userData.avatar || "",
        referralCode: "",
        agentCode,
      }

      let user: null | IUser = null
      if (rootStore.loggedInUser) {
        const updatedUser = await updateUser(rootStore.loggedInUser.id, userPayload)
        user = updatedUser.user
      } else {
        const { createdUser } = await createUser(userPayload)
        user = createdUser
      }

      store.setCreatedUser(user)
    },
    onSuccess: () => {
      store.setCurrentStep("subscription")
    },
  })

  const { mutate: signUpUser } = useMutation({
    mutationFn: async (userData: Partial<FormUserData>) => {
      store.setCurrentStep("slackInformation")
      store.setIsPendingRequest(true)

      const agentCode = currentUser?.uid
      const createdUser = toJS(store.createdUser)
      const agentLink = await generateAgentLink(agentCode, createdUser)

      const agentProfile: AgentProfilePayload = {
        email: currentUser?.email as string,
        phoneNumber: userData.phoneNumber || "",
        shortBio: userData.shortBio || "",
        favouriteTravelDestination: userData.favoriteTravelDestination || "",
        bestTravelAdvice: userData.bestTravelAdvice || "",
        agencyName: userData.agencyName || "",
        agencyPhoneNumber: userData.agencyPhoneNumber || "",
        cruisesOverallVolumePercent: userData.cruisesOverallVolumePercent,
        cruisesAnnualBookingVolume: userData.cruisesAnnualBookingVolume,
        primaryCustomerAcquisitionMean: userData.primaryCustomerAcquisitionMean,
        agencyGoalsAtCruisen: userData.agencyGoalsAtCruisen || [],
        hostAgencyId: Number(userData.hostAgencyId) || null,
        consortiaId: Number(userData.consortiaId) || null,
        agentCode,
        agentLink,
        plan: AgentPlanEnum.LITE,
        preferredCruiseLineIds: [],
      }

      const upsertedAgentProfile = await upsertAgentProfile({
        agentProfile: {
          userId: createdUser?.id,
          ...agentProfile,
        },
      })

      const fbClickData = load<{ fbclid: string; fbclidCreatedAt: string }>("fbClickData")
      Analytics.logSignUp({
        fbclid: fbClickData?.fbclid,
        fbclidCreatedAt: fbClickData?.fbclidCreatedAt,
      })

      if (!rootStore.isCruisenDeveloper && isSubscriptionEnabled()) {
        await createStripeSubscription({
          priceId: stripeConfigPrice.priceId,
          couponId: stripeConfigPrice.defaultCouponId,
        })
      }

      const trips = await getUserTrips(createdUser?.id || "")
      let activeTrip: ITrip | null = null

      if (trips.length === 0) {
        const createdTrip = await prepareDemoTrip({ createdUser })
        activeTrip = createdTrip
      } else {
        activeTrip = trips[0]
      }
      store.setIsUserSignuped(true)
      const newParams = new URLSearchParams(searchParams)
      newParams.set("isSignuped", "true")
      setSearchParams(newParams)

      // We delay this to be ensure that search params are updated
      setTimeout(() => {
        rootStore.setProp("agentProfile", upsertedAgentProfile)
        rootStore.setProp("loggedInUser", UserModel.create(createdUser))
        rootStore.setProp("activeTrip", activeTrip)
        queryClient.invalidateQueries({ queryKey: ["agentProfile", rootStore.loggedInUser.id] })
        store.setIsPendingRequest(false)
      }, 500)
    },
  })

  useEffect(() => {
    if (store.isUserSignuped || store.isPendingRequest) return

    if (!currentUser) {
      navigate("/")
    } else if (currentUser && rootStore.loggedInUser) {
      store.setUserData({
        firstName: rootStore.loggedInUser?.firstName || "",
        lastName: rootStore.loggedInUser?.lastName || "",
        phoneNumber: rootStore.loggedInUser?.phoneNumber || "",
        avatar: rootStore.loggedInUser?.avatar || "",
      })
      store.setCurrentStep("profileInformation")
    }
  }, [currentUser])

  const navigateToDashboard = () => {
    navigate("/dashboard")
  }

  const currentStepIndex =
    variants.find((variant) => variant.step === store.currentStep)?.stepIndex || 0

  return (
    <FormStepCard
      image={currentVariant.image}
      title={currentVariant.title}
      description={currentVariant?.description || ""}
      currentStepIndex={currentStepIndex}
    >
      {match(store.currentStep)
        .with("personalDetails", () => (
          <PersonalDetails
            formValues={{
              firstName: store.userData?.firstName || "",
              lastName: store.userData?.lastName || "",
              phoneNumber: store.userData?.phoneNumber || "",
            }}
            isUserRegistrationPending={isUserRegistrationPending}
            onSubmit={(data) => {
              store.setUserData(data)
              mutateRegisterUser(store.userData)
            }}
            termsAccepted={store.termsAccepted}
            setTermsAccepted={() => store.setTermsAccepted(!store.termsAccepted)}
          />
        ))
        .with("profileInformation", () => (
          <ProfileInformation
            formValues={{
              shortBio: store.userData?.shortBio || "",
              favoriteTravelDestination: store.userData?.favoriteTravelDestination || "",
              bestTravelAdvice: store.userData?.bestTravelAdvice || "",
              avatar: store.userData?.avatar || "",
            }}
            onSubmit={(data) => {
              store.setUserData(data)
              store.setCurrentStep("agencyInformation")
            }}
            goBack={(data) => {
              store.setUserData(data)
              store.setCurrentStep("personalDetails")
            }}
          />
        ))
        .with("agencyInformation", () => (
          <AgencyInformation
            formValues={{
              agencyName: store.userData?.agencyName || "",
              agencyPhoneNumber: store.userData?.agencyPhoneNumber || "",
              hostAgencyId: store.userData?.hostAgencyId || "",
              consortiaId: store.userData?.consortiaId || "",
            }}
            onSubmit={(data) => {
              store.setUserData(data)
              store.setCurrentStep("agencySurvey")
            }}
            goBack={(data) => {
              store.setUserData(data)
              store.setCurrentStep("profileInformation")
            }}
          />
        ))
        .with("agencySurvey", () => (
          <AgencySurvey
            formValues={{
              cruisesOverallVolumePercent: store.userData?.cruisesOverallVolumePercent,
              cruisesAnnualBookingVolume: store.userData?.cruisesAnnualBookingVolume,
              primaryCustomerAcquisitionMean: store.userData?.primaryCustomerAcquisitionMean,
              agencyGoalsAtCruisen: store.userData?.agencyGoalsAtCruisen || [],
            }}
            isUserUpsertPending={isUserUpsertPending}
            onSubmit={(data) => {
              store.setUserData(data)
              if (store.createdUser) {
                store.setCurrentStep("subscription")
              } else {
                mutateUpsertUser(store.userData)
              }
            }}
            goBack={(data) => {
              store.setUserData(data)
              store.setCurrentStep("agencyInformation")
            }}
          />
        ))
        .with("subscription", () => (
          <Subscription
            onSubmit={() => {
              signUpUser(store.userData)
            }}
            goBack={() => {
              store.setCurrentStep("agencySurvey")
            }}
            stripeConfigPrice={stripeConfigPrice}
          />
        ))
        .with("slackInformation", () => (
          <SlackInformation
            navigateToDashboard={navigateToDashboard}
            isLoading={store.isPendingRequest}
          />
        ))
        .otherwise(() => (
          <div></div>
        ))}
    </FormStepCard>
  )
})

export default SignUpForm
